import { useCallback, useState } from "react";

import { useChatSessions } from "./useChatSession";
import { useSelectionMemory } from "./chatSelectionMemory";

/**
 * Selection + history-toggle state shared by the docked chat surfaces (the
 * floating assistant card and the steering dock).
 *
 * Opens blank: nothing picks a conversation for the user. A session goes active
 * only through `selectSession` — a History row, the session ChatWindow lazily
 * creates on first send, or one handed down by a parent — and that choice is
 * remembered per tab, so a reload keeps it while a new tab starts fresh.
 */
export function useDockedChat(surface: string) {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null,
  );
  // Whether the past-conversations list is showing instead of the chat.
  const [showList, setShowList] = useState(false);

  const remember = useSelectionMemory(
    surface,
    useChatSessions(),
    setSelectedSessionId,
  );

  const startNewChat = useCallback(() => {
    remember(null);
    setSelectedSessionId(null);
    setShowList(false);
  }, [remember]);

  // Closing the list is harmless when it's already closed (ChatWindow's
  // onSessionCreated fires mid-conversation).
  const selectSession = useCallback(
    (id: string | null) => {
      remember(id);
      setSelectedSessionId(id);
      setShowList(false);
    },
    [remember],
  );

  return {
    selectedSessionId,
    showList,
    setShowList,
    startNewChat,
    selectSession,
  };
}
